import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { DatePicker, ReactDatePickerValue } from '../../elements/DatePicker';
import SelectInput from '../../elements/SelectInput';
import dayjs from 'dayjs';
import { Table } from '../Table';
import { columns } from '../../../helper/fileListDetails';
import { IRow } from '../../../types/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../elements/Loader';
import { FileDetails, FileStorageLink } from '../../../types/location';
import { SelectInputOption } from '../../../types/sharedTypes';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useFileUpload } from '../../../context/LocationUploadContext';
import { ApiEndpoint } from '../../../constants/endpoints';
import { useQuery } from '@tanstack/react-query';
import api from '../../../lib/api';
import { getXprops } from '../../../hooks/useXProps';

dayjs.extend(timezone);
dayjs.extend(utc);

const Schema = zod.object({
  startDate: zod.date().optional(),
  endDate: zod.date().optional(),
  uploadedBy: zod.string(),
});

type FormSchemaType = zod.infer<typeof Schema>;

const FileList = () => {
  const {
    filterSelectedUser,
    filterStartDate,
    filterEndDate,
    handleFileListDateChange,
    handleFileListUserChange,
    locationFiles,
    isLoadingLocationFiles,
    isRefetchingLocationFiles,
    setFilterSelectedUser,
  } = useFileUpload();

  const { tenant } = getXprops();
  const { register, watch, setValue } = useForm<FormSchemaType>({
    resolver: zodResolver(Schema),
    mode: 'all',
    defaultValues: {
      startDate: filterStartDate,
      endDate: filterEndDate,
      uploadedBy: filterSelectedUser?.cur,
    },
  });

  const [data, setData] = useState<IRow>({});
  const [users, setUsers] = useState<SelectInputOption[]>([]);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [selectedFileDate, setSelectedFileDate] = useState<string>('');

  const {
    data: fileStorageLinkData,
    isLoading: isLoadingFileStorageLink,
    isRefetching: isRefetchingFileStorageLink,
  } = useQuery<FileStorageLink, ApiEndpoint>({
    queryKey: ['file-storage-link'],
    queryFn: () =>
      api.fetch('file_storage_link', {
        timeframe: selectedFileDate,
        tenant: tenant,
        filename: selectedFileName,
      }),
    enabled: !!tenant && !!selectedFileName,
  });

  const isFetchingFileStorageLink = useMemo(
    () => isRefetchingFileStorageLink || isLoadingFileStorageLink,
    [isRefetchingFileStorageLink, isLoadingFileStorageLink],
  );
  const isFetchingLocationFiles = useMemo(
    () => isRefetchingLocationFiles || isLoadingLocationFiles,
    [isRefetchingLocationFiles, isLoadingLocationFiles],
  );
  const handleFileSelection = useCallback(
    (identifier: string, index: string) => {
      let filteredFiles = locationFiles?.data.file_list as FileDetails[];
      if (identifier === columns[2]?.identifier) {
        if (locationFiles?.data.user_list.includes(filterSelectedUser.cur)) {
          filteredFiles = filteredFiles?.filter(
            (file) => file.username === filterSelectedUser.cur,
          );
        }

        const selectedRow = filteredFiles[Number(index)];

        setSelectedFileName(`${selectedRow.datetime}_${selectedRow.filename}`);
        setSelectedFileDate(
          dayjs(new Date(Number(selectedRow.datetime))).format('YYYY/MM/DD'),
        );
      } else return null;
    },
    [locationFiles, filterSelectedUser],
  );

  const handleDateChange = (newDates: ReactDatePickerValue) => {
    setValue('startDate', newDates[0]);
    setValue('endDate', newDates[1]);
    handleFileListDateChange(newDates);
  };

  const handleUserChange = (username: string) => {
    handleFileListUserChange(username);
    setValue('uploadedBy', username);
  };

  const formatLocationFilesData = (locationFiles: FileDetails[]) => {
    return (locationFiles as unknown as FileDetails[])?.map(
      (file: FileDetails) => {
        const fileDate = new Date(Number(file.datetime));

        const datetime = dayjs(fileDate)
          .utc()
          .tz('EST5EDT')
          .format('MMMM D, YYYY h:mm A (UTCZ)');

        return {
          datetime: datetime,
          username: file.username,
          filename: file.filename,
        };
      },
    );
  };

  useEffect(() => {
    if (locationFiles?.data) {
      let fileListData =
        formatLocationFilesData(
          locationFiles?.data.file_list as FileDetails[],
        ) ?? {};

      if (locationFiles.data?.user_list?.includes(filterSelectedUser?.cur))
        fileListData = fileListData.filter(
          (file) => file.username === filterSelectedUser?.cur,
        );

      const userData = locationFiles.data.user_list.map((item) => {
        return {
          label: item,
          value: item,
        };
      });

      setData(fileListData as unknown as IRow);
      setUsers(userData);
    }
  }, [locationFiles, isLoadingLocationFiles]);

  useEffect(() => {
    const { prev, cur } = filterSelectedUser;
    const fileListData = formatLocationFilesData(
      locationFiles?.data?.file_list as FileDetails[],
    );

    if (prev === cur && prev !== '') {
      setFilterSelectedUser({ prev: '', cur: '' });
      setValue('uploadedBy', '');
    }

    if (cur) {
      const filteredData = fileListData?.filter(
        (item) => item.username === cur,
      ) as unknown as IRow;
      setData(filteredData);
    } else setData(fileListData as unknown as IRow);
  }, [filterSelectedUser]);

  useEffect(() => {
    if (
      selectedFileName &&
      fileStorageLinkData?.data &&
      !isFetchingFileStorageLink
    ) {
      const a = document.createElement('a');
      a.setAttribute('href', fileStorageLinkData?.data);
      a.click();
      a.remove();
      setSelectedFileName('');
    }
  }, [selectedFileName, fileStorageLinkData?.data, isFetchingFileStorageLink]);

  return (
    <div className='h-full' data-test-id='file-list'>
      <div className='flex flex-col bg-white rounded-lg w-full h-full p-2 border border-gray'>
        <div
          className='p-10 bg-gray-lighter h-full'
          data-test-id='file-list-table-wrapper'
        >
          <div className='flex'>
            <div className='w-full max-w-[200px] mr-8'>
              <DatePicker
                onChange={(e) => handleDateChange(e.target.value)}
                value={[watch('startDate'), watch('endDate')]}
                name='datepicker'
              />
            </div>
            <div className='w-full max-w-[200px]'>
              <SelectInput
                options={users}
                value={watch('uploadedBy')}
                {...register('uploadedBy')}
                onChange={(event) =>
                  handleUserChange(event.target.value as string)
                }
                placeholder='Uploaded By'
                disabled={!users.length}
              />
            </div>
          </div>

          {isFetchingLocationFiles ? (
            <div className='flex justify-center items-center w-full h-[60%]'>
              <Loader fillColor='#6923F4' size='40px' />
            </div>
          ) : Object.keys(data || {})?.length ? (
            <Table
              data={data}
              columns={columns}
              dataTestIds={{ table: 'file-list-table' }}
              className={{
                header: 'bg-white border border-b-1 border-gray-light',
                headerColumn: 'border-r',
                table: 'max-h-[70%] md:max-h-[85%]',
              }}
              onCellClick={handleFileSelection}
              isFetchingCellData={isFetchingFileStorageLink}
            />
          ) : (
            <div className='w-full flex justify-center p-4'>
              <p className='text-gray-700'>
                No results for the configured filter(s)
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileList;
